<template>
	<el-dialog
		:modelValue="open"
		@update:modelValue="$emit('update:open', $event)"
		:title="'Rückfrage stellen'"
		@close="handleClose"
		:before-close="(done) => dialogBeforeClose(done, waiting)"
	>
		<TheForm
			:model="request"
			:rules="rules"
			:waiting="waiting"
			:waitingMsg="texts.formErrors.waiting"
			ref="formRef"
			:showErr="showErr"
			:errMsg="errMsg"
			:id="id"
		>
			<el-form-item label="Enddatum" prop="validToDate">
				<el-date-picker v-model="request.validToDate" type="date" placeholder="TT.MM.JJJJ" :format="'DD.MM.YYYY'" :disabled-date="disabledDate" />
			</el-form-item>

			<el-form-item label="Freitexteingabe" prop="text">
				<el-input v-model="request.text" :autosize="{ minRows: 10 }" type="textarea" placeholder="Bitte Rückfrage hier eingeben." />
			</el-form-item>
		</TheForm>

		<template #footer>
			<div class="button-panel">
				<TheButton type="danger" @buttonclick="save" :disabled="waiting" :loading="waiting">Senden</TheButton>
				<TheButton type="default" @buttonclick="handleClose">Abbrechen</TheButton>
			</div>
		</template>
	</el-dialog>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { texts, translateToGerman } from '../../utils/texts';
import TheButton from '../TheButton.vue';
import TheForm from '../TheForm.vue';
import { createCallback } from '@/requests/callbacks';
import { CallbackRequest } from '@/types/types';
import { resetForm, validateForm, dialogBeforeClose, getEndOfDayWithTime } from '@/utils/functions';
import type { FormInstance } from 'element-plus';
import { checkIfDateInPast } from '@/utils/search/searchUtils';

interface CallbackRequestData {
	request: Partial<CallbackRequest>;
	rules: Object;
	id: string;
	waiting: boolean;
	showErr: boolean;
	errMsg: string;
}

export default defineComponent({
	name: 'DialogCreateCallback',
	data(): CallbackRequestData {
		return {
			request: {
				validToDate: '',
				text: '',
			},
			rules: {
				validToDate: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' }],
				text: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur'] },
					{ min: 3, message: texts.formErrors.minChars, trigger: 'blur' },
				],
			},
			id: 'createCallbackForm',
			waiting: false,
			showErr: false,
			errMsg: texts.callbacks.saveFailed,
		};
	},
	components: {
		TheForm,
		TheButton,
	},
	props: {
		open: {
			type: Boolean,
			required: true,
		},
		complaintId: {
			type: String,
			required: true,
		},
	},
	setup() {
		const formRef = ref<FormInstance | null>(null);

		return {
			formRef,
		};
	},
	computed: {
		texts() {
			return texts;
		},
	},
	emits: ['reload', 'update:open'],
	methods: {
		disabledDate(date) {
			return checkIfDateInPast(date, false);
		},
		async save() {
			const isValid = await validateForm(this.formRef, this.id);
			if (!isValid) return;

			this.waiting = true;

			let validToDate = this.request.validToDate;
			if (this.request.validToDate) {
				validToDate = getEndOfDayWithTime(this.request.validToDate, true);
			}

			const requestCreateCallback = {
				complaintId: String(this.complaintId),
				text: this.request.text,
				validToDate: validToDate,
			} as CallbackRequest;

			const result = await createCallback(requestCreateCallback);
			if (result.success) {
				this.handleClose(true);
			} else {
				const msg = translateToGerman(result.error);
				if (msg) {
					this.errMsg = msg;
				}

				this.showErr = true;
			}

			this.waiting = false;
		},
		handleClose(reload: Boolean) {
			resetForm(this.formRef, this.id);
			this.showErr = false;

			if (reload) {
				this.$emit('reload');
			}
			this.$emit('update:open', false);
		},
		dialogBeforeClose,
	},
});
</script>

<style lang="scss"></style>
